import * as React from 'react';
import hoistNonReactStatic from 'hoist-non-react-statics';
import { useUpdateEffect } from 'react-use';
import { isEqual } from 'lodash';

import { PermissionStatusEnum } from '@doltech/utils/lib/constants';

interface AudioInputDevicesModel {
  mediaStream?: MediaStream;
  isMicrophoneAllowed?: boolean;
  isMicrophoneDisabled?: boolean;
  devices?: MediaDeviceInfo[];
  loadDevices?: () => Promise<void>;
  permissionStatus?: PermissionStatusEnum;
  selectedDevice?: MediaDeviceInfo;
  loadMediaStream?: () => Promise<MediaStream>;
  stopMediaStream?: (inputStream?: MediaStream) => void;
  checkAudioInputPermission?: () => Promise<{
    stream?: MediaStream;
    permissionStatus?: PermissionStatusEnum;
  }>;
  changeAudioInput?: (selectedDevice: MediaDeviceInfo) => void;
}

const AudioInputDevicesContext = React.createContext<AudioInputDevicesModel>({});

export const useAudioInputDevices = (): AudioInputDevicesModel =>
  React.useContext(AudioInputDevicesContext);

export const withAudioInputDevices = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const ResponseComponent = (props: P) => {
    const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
    const [selectedDevice, setSelectedDevice] = React.useState<MediaDeviceInfo>(null);
    const [mediaStream, setMediaStream] = React.useState<MediaStream>(null);
    const [permissionStatus, setPermissionStatus] = React.useState<PermissionStatusEnum>(
      PermissionStatusEnum.NOT_DETERMINED
    );

    useUpdateEffect(() => {
      if (devices?.length) {
        setSelectedDevice(devices[0]);
      }
    }, [devices]);

    const loadMediaStream = () => {
      const constraints = {
        video: false,
        audio: {
          deviceId: selectedDevice?.deviceId,
        },
      };
      return navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
        setMediaStream(stream);
        return stream;
      });
    };

    const stopMediaStream = (inputStream?: MediaStream) => {
      const stream = inputStream || mediaStream;
      if (stream) {
        stream.getAudioTracks().forEach((track) => track.stop());
      }
      setMediaStream(null);
    };

    const checkAudioInputPermission = async () => {
      let stream: MediaStream;
      let updatedPermissionStatus;

      try {
        stream = await loadMediaStream();
        updatedPermissionStatus = PermissionStatusEnum.ALLOWED;
      } catch (e) {
        updatedPermissionStatus = PermissionStatusEnum.DENIED;
      }
      setPermissionStatus(updatedPermissionStatus);
      return {
        stream,
        permissionStatus: updatedPermissionStatus,
      };
    };

    const loadDevices = async () => {
      const { stream } = await checkAudioInputPermission();

      try {
        const userDevices = await navigator.mediaDevices.enumerateDevices();
        setDevices((oldDevices) => {
          const newDevices = userDevices.filter((device) => device.kind === 'audioinput');
          const newDeviceIds = newDevices.map(({ deviceId }) => deviceId);
          const oldDeviceIds = oldDevices.map(({ deviceId }) => deviceId);

          if (!isEqual(newDeviceIds, oldDeviceIds)) {
            return newDevices;
          }

          return oldDevices;
        });
      } catch (e) {
        console.error(e.message);
      }
      stopMediaStream(stream);
    };

    const changeAudioInput = (device) => setSelectedDevice(device);

    return (
      <AudioInputDevicesContext.Provider
        value={{
          mediaStream,
          isMicrophoneAllowed: PermissionStatusEnum.ALLOWED === permissionStatus,
          isMicrophoneDisabled: PermissionStatusEnum.DENIED === permissionStatus,
          loadDevices,
          permissionStatus,
          devices,
          selectedDevice,
          loadMediaStream,
          stopMediaStream,
          checkAudioInputPermission,
          changeAudioInput,
        }}
      >
        <Component {...props} />
      </AudioInputDevicesContext.Provider>
    );
  };

  hoistNonReactStatic(ResponseComponent, Component);

  return ResponseComponent;
};
